import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  Select,
  PasswordInput,
  Text
} from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { CreateUserCommand } from '@/application/dto/UserDtos';
import { useCreateUserMutation } from '@/application/queries/userQueries';
import { useAuth } from '../../hooks/useAuth';
import { useSearchBusinessesQuery } from '@/application/queries/businessQueries';

export interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * CreateUserModal - Modal for creating a new user (admin only)
 *
 * Features:
 * - Form with required fields (userName, email, password, role)
 * - Optional fields (firstName, lastName, displayName, phoneNumber)
 * - Business assignment for BusinessOwner role
 * - Password visibility toggle
 * - Form validation
 * - Success/error notifications via mutation hook
 * - Loading state during save
 */
export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const createUserMutation = useCreateUserMutation();

  // Form state
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    displayName: '',
    phoneNumber: '',
    role: '' as 'Admin' | 'BusinessOwner' | '',
    businessId: ''
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Business search for BusinessOwner role
  const [businessSearchQuery, setBusinessSearchQuery] = useState('');
  const { data: businessResults } = useSearchBusinessesQuery(
    businessSearchQuery,
    10,
    undefined,
    formData.role === 'BusinessOwner' && businessSearchQuery.length > 0
  );

  /**
   * Reset form when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      setFormData({
        userName: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        displayName: '',
        phoneNumber: '',
        role: '',
        businessId: ''
      });
      setErrors({});
      setBusinessSearchQuery('');
    }
  }, [isOpen]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback(
    (field: string, value: string): void => {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }

      // Clear businessId if role changes from BusinessOwner
      if (field === 'role' && value !== 'BusinessOwner') {
        setFormData((prev) => ({ ...prev, businessId: '' }));
      }
    },
    [errors]
  );

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Username is required
    if (!formData.userName || formData.userName.trim() === '') {
      newErrors['userName'] = 'Username is required';
    } else if (formData.userName.length < 3) {
      newErrors['userName'] = 'Username must be at least 3 characters';
    }

    // Email is required and must be valid
    if (!formData.email || formData.email.trim() === '') {
      newErrors['email'] = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors['email'] = 'Invalid email format';
    }

    // Password is required and must meet strength requirements
    if (!formData.password || formData.password.trim() === '') {
      newErrors['password'] = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors['password'] = 'Password must be at least 6 characters';
    }

    // Role is required
    if (!formData.role || formData.role.trim() === '') {
      newErrors['role'] = 'Role is required';
    }

    // BusinessOwner role requires businessId
    if (formData.role === 'BusinessOwner' && !formData.businessId) {
      newErrors['businessId'] = 'Business assignment is required for Business Owner role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!validate()) {
      return;
    }

    try {
      // Build CreateUserCommand
      const command: CreateUserCommand = {
        userName: formData.userName,
        email: formData.email,
        password: formData.password,
        role: formData.role as 'Admin' | 'BusinessOwner',
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        displayName: formData.displayName || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        businessId: formData.role === 'BusinessOwner' ? formData.businessId : undefined
      };

      await createUserMutation.mutateAsync({
        command,
        userRole: user?.role ?? 'Admin'
      });

      // Close modal and notify parent
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by mutation hook (notifications)
      console.error('Create user failed:', error);
    }
  }, [formData, validate, createUserMutation, user, onClose, onSuccess]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  // Prepare business options for select
  const businessOptions =
    businessResults?.map((business) => ({
      value: business.id,
      label: business.name
    })) ?? [];

  return (
    <Modal opened={isOpen} onClose={onClose} title="Create New User" size="lg" centered>
      <Stack gap="md">
        {/* Username (Required) */}
        <TextInput
          label="Username"
          placeholder="Enter username"
          value={formData.userName}
          onChange={(e) => handleChange('userName', e.currentTarget.value)}
          error={errors['userName']}
          required
          withAsterisk
        />

        {/* Email (Required) */}
        <TextInput
          label="Email"
          placeholder="user@example.com"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.currentTarget.value)}
          error={errors['email']}
          required
          withAsterisk
        />

        {/* Password (Required) */}
        <PasswordInput
          label="Password"
          placeholder="Enter password"
          value={formData.password}
          onChange={(e) => handleChange('password', e.currentTarget.value)}
          error={errors['password']}
          required
          withAsterisk
        />

        {/* First Name & Last Name */}
        <Group grow>
          <TextInput
            label="First Name"
            placeholder="John"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.currentTarget.value)}
          />

          <TextInput
            label="Last Name"
            placeholder="Doe"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.currentTarget.value)}
          />
        </Group>

        {/* Display Name */}
        <TextInput
          label="Display Name"
          placeholder="Optional display name"
          value={formData.displayName}
          onChange={(e) => handleChange('displayName', e.currentTarget.value)}
        />

        {/* Phone Number */}
        <TextInput
          label="Phone Number"
          placeholder="(555) 123-4567"
          value={formData.phoneNumber}
          onChange={(e) => handleChange('phoneNumber', e.currentTarget.value)}
        />

        {/* Role (Required) */}
        <Select
          label="Role"
          placeholder="Select user role"
          value={formData.role}
          onChange={(value) => handleChange('role', value ?? '')}
          data={[
            { value: 'Admin', label: 'Admin' },
            { value: 'BusinessOwner', label: 'Business Owner' }
          ]}
          error={errors['role']}
          required
          withAsterisk
        />

        {/* Business Assignment (Required for BusinessOwner) */}
        {formData.role === 'BusinessOwner' && (
          <>
            <Select
              label="Business"
              placeholder="Search and select business"
              value={formData.businessId}
              onChange={(value) => handleChange('businessId', value ?? '')}
              data={businessOptions}
              error={errors['businessId']}
              required
              withAsterisk
              searchable
              onSearchChange={setBusinessSearchQuery}
              nothingFoundMessage="No businesses found"
            />
            <Text size="xs" c="dimmed">
              Business assignment is required for Business Owner role
            </Text>
          </>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" leftSection={<IconX size={16} />} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={createUserMutation.isPending}
            disabled={createUserMutation.isPending}
          >
            Create User
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
